import { useEffect, useMemo, useState } from 'react'

import { Box } from '@mui/material'
import { getFirstAndLastYear } from 'utils/getDataAIMetricsOT'
import { useActions, useAppSelector } from 'hooks'
import { inputFilter } from 'store/AIMetricOT/reducerSlice'
import { DOWNLOAD_FILE_NAME } from 'constant'
import DownloadImages from 'components/common/Download/DownloadImages'
import { useImageDownload } from 'components/common/Download/useImageDownload'
import { DownloadType } from 'types'
import ChartHeader from 'components/common/ChartHeader/ChartHeader'
import { getMetricByKey } from 'utils/getMetricListByType'
import { getAllMetricsFromGroups } from 'utils/getAllMetricsFromGroups'
import LineChartOT from './LineChart'
import { getLineChartData } from 'utils/getLineChartData'

const LineChartWrapper = () => {
  const currentFilter = useAppSelector(inputFilter)
  const metricDataTypeMap = useAppSelector(({ vibrancy }) => vibrancy.metricDataTypeMap)
  const multiSelectedCountries = useAppSelector(({ AIMetric }) => AIMetric.multiSelectedCountries)

  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const yearRange = useAppSelector(({ vibrancy }) => vibrancy.configuration.yearFilter.yearRange)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const allMetrics = getAllMetricsFromGroups(metricGroups)
  const fileNameDownload = DOWNLOAD_FILE_NAME.aiMetricOverTime
  const [clickDownload, setClickDownload] = useState<DownloadType>(null)
  const { handleCallDownload, divRef } = useImageDownload()
  const { setShowDwnLoader } = useActions()
  const getMetricName = getMetricByKey(
    allMetrics,
    currentFilter.metric[currentFilter.metricType as keyof typeof currentFilter.metric]?.[currentFilter.metricValue],
  )

  const metricDetails = useMemo(() => {
    const allDetailedMetrics: any = []
    metricGroups.forEach((group: any) => {
      if (group?.metrics?.length) {
        allDetailedMetrics.push(...group?.metrics)
      }
    })
    const shortName =
      currentFilter.metric[currentFilter.metricType as keyof typeof currentFilter.metric]?.[currentFilter.metricValue]
    const findMetric = allDetailedMetrics.find((m: any) => {
      return m.shortname_raw === shortName && m?.metricValue === currentFilter.metricValue
    })
    return findMetric
  }, [metricGroups, currentFilter])

  const showPercentage = useMemo(() => {
    return metricDataTypeMap[metricDetails?.shortname_raw] === 'Percentage'
  }, [metricDataTypeMap, metricDetails])

  const linechartData = useMemo(() => {
    return getLineChartData(countryData, multiSelectedCountries, metricDetails, showPercentage, yearRange)
  }, [currentFilter, multiSelectedCountries])

  const vendorName = useMemo(() => {
    return metricDetails?.source
  }, [metricDetails])

  const currentYearHaveData = getFirstAndLastYear(linechartData)
  const yearDataRender = () => {
    if (currentYearHaveData?.firstYear === currentYearHaveData?.lastYear) {
      return `${currentYearHaveData?.firstYear || 2024}`
    }
    return `${currentYearHaveData?.firstYear || 2017}–${currentYearHaveData?.lastYear || 2024}`
  }

  // Download images
  const handleDivDownload = (type: DownloadType) => {
    setShowDwnLoader(true)
    setClickDownload(type)
  }

  useEffect(() => {
    if (clickDownload) {
      handleCallDownload(clickDownload, {
        fileName: fileNameDownload,
        orientation: 'landscape',
        isSquareDownload: true,
      })
      setClickDownload(null)
    }
  }, [clickDownload])

  const yearSource = new Date().getFullYear()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      {getMetricName ? (
        <DownloadImages
          handleDownPNG={() => handleDivDownload('png')}
          handleDownJPG={() => handleDivDownload('jpg')}
          handleDownPDF={() => handleDivDownload('pdf')}
        />
      ) : null}
      <Box sx={{ padding: '24px' }}>
        <Box
          sx={{
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <ChartHeader
            header1={
              getMetricName
                ? `${getMetricName}${currentFilter.metricValue === 'perCapita' ? ' Per Capita' : ''}, ${yearDataRender()}`
                : 'No data available'
            }
            header2={getMetricName ? `Source: ${vendorName} | Chart: AI Index, ${yearSource}` : ''}
          />
        </Box>
        {getMetricName ? (
          <LineChartOT
            showPercentage={showPercentage}
            data={linechartData}
            isTickLine={false}
            height={500}
            tickColor={'#000'}
            axisFontSize={14}
            tickCount={8}
            selectedCountries={multiSelectedCountries}
          />
        ) : null}
      </Box>
      {getMetricName ? (
        <Box sx={{ zIndex: -1, position: 'absolute', padding: '24px', minWidth: '1020px' }} ref={divRef}>
          <Box
            sx={{
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <ChartHeader
              header1={`${getMetricName}${currentFilter.metricValue === 'perCapita' ? ' Per Capita' : ''}, ${yearDataRender()}`}
              header2={`Source: ${vendorName} | Chart: AI Index, ${yearSource}`}
            />
          </Box>
          <LineChartOT
            showPercentage={showPercentage}
            data={linechartData}
            isTickLine={false}
            height={500}
            tickColor={'#000'}
            axisFontSize={14}
            tickCount={8}
            selectedCountries={multiSelectedCountries}
          />
        </Box>
      ) : null}
    </Box>
  )
}

export default LineChartWrapper
